import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import KPICard from './components/KPICard';
import ApplicantFunnelChart from './components/ApplicantFunnelChart';
import PayrollTrendChart from './components/PayrollTrendChart';
import InvoiceAgingChart from './components/InvoiceAgingChart';
import RecentActivityFeed from './components/RecentActivityFeed';
import PendingActionItems from './components/PendingActionItems';
import dashboardService from '../../services/dashboardService';

export default function DashboardOverview() {
  const { user, userProfile, loading: authLoading } = useAuth();
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [payrollStats, setPayrollStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [payrollTrendData, setPayrollTrendData] = useState([]);
  const [agingData, setAgingData] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const isEmployee = userProfile?.role === 'employee';

  // Redirect employees to their self-service portal
  useEffect(() => {
    if (!authLoading && userProfile) {
      if (userProfile.role === 'employee') {
        console.log('🔄 Employee detected, redirecting to employee self-service portal');
        navigate('/erp/employee-self-service', { replace: true });
      }
    }
  }, [userProfile, authLoading, navigate]);

  // Mock notifications - will be replaced with real data later
  const mockNotifications = [
    {
      id: 1,
      type: 'warning',
      title: 'Compliance Alert',
      message: 'PF return filing due in 3 days for June 2025',
      timestamp: new Date(Date.now() - 300000),
      read: false
    },
    {
      id: 2,
      type: 'success',
      title: 'Payroll Processed',
      message: 'July 2025 salary processed successfully for employees',
      timestamp: new Date(Date.now() - 14400000),
      read: false
    },
    {
      id: 3,
      type: 'info',
      title: 'New Application',
      message: 'New application received for Senior Facility Manager position',
      timestamp: new Date(Date.now() - 28800000),
      read: true
    }
  ];

  // Toast notifications
  const [toastNotifications, setToastNotifications] = useState([]);

  // Move loadDashboardData function outside useEffect to make it reusable
  const loadDashboardData = async () => {
    if (!user || isEmployee) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch data in parallel
      const [kpiResult, activitiesResult, funnelResult, payrollTrendResult, agingResult, pendingResult] = await Promise.all([
        dashboardService.getKPIData(),
        dashboardService.getRecentActivity(),
        dashboardService.getApplicantFunnelData(),
        dashboardService.getPayrollTrendData(),
        dashboardService.getInvoiceAgingData(),
        dashboardService.getPendingActions()
      ]);

      if (kpiResult.success) setKpiData(kpiResult.data);
      if (activitiesResult.success) setRecentActivities(activitiesResult.data);
      if (funnelResult.success) setFunnelData(funnelResult.data);
      if (payrollTrendResult.success) setPayrollTrendData(payrollTrendResult.data);
      if (agingResult.success) setAgingData(agingResult.data);
      if (pendingResult.success) setPendingActions(pendingResult.data);

      if (!kpiResult.success) throw new Error(kpiResult.error);

    } catch (error) {
      console.log('Dashboard data loading error:', error);
      setError(error?.message || 'Failed to load dashboard data. Please check your database connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Update current time every minute
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (user && userProfile && !isEmployee) {
      loadDashboardData();

      // Show welcome notification
      const welcomeNotification = {
        id: Date.now(),
        type: 'info',
        title: 'Welcome Back!',
        message: `Good ${getTimeOfDay()}, ${userProfile?.full_name || user?.email}. Here's your dashboard overview.`,
        timestamp: new Date(),
        persistent: false
      };

      setToastNotifications([welcomeNotification]);
    } else {
      setLoading(false);
    }
  }, [user, userProfile, authLoading, isEmployee]);

  const getTimeOfDay = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleNotificationDismiss = (id) => {
    setToastNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Show loading state during auth initialization
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show preview mode for unauthenticated users
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        {/* Preview Mode Banner */}
        <div className="bg-amber-100 border-b border-amber-200 px-4 py-3">
          <div className="flex items-center justify-center">
            <div className="text-amber-800 text-sm font-medium">
              📋 Preview Mode - Sign in to access your personalized dashboard
            </div>
          </div>
        </div>

        {/* Header Navigation - Preview Mode */}
        <HeaderNavigation
          onSidebarToggle={handleSidebarToggle}
          user={null}
          notifications={mockNotifications}
          previewMode={true}
        />

        {/* Main Sidebar */}
        <MainSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content Area */}
        <div
          className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}
        >
          <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)]">
            {/* Dashboard Content */}
            <div
              className="flex-1 overflow-auto transition-all duration-300"
              style={{
                paddingRight: '0'
              }}
            >
              <div className="p-4 sm:p-6">
                {/* Breadcrumb Navigation */}
                <div className="mb-6">
                  <BreadcrumbNavigation />
                </div>

                {/* Dashboard Header */}
                <div className="mb-6 sm:mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dashboard Overview</h1>
                      <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
                        Preview of technical staff placement operations dashboard.
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {currentTime.toLocaleDateString('en-IN', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-base sm:text-lg font-semibold text-foreground">
                        {currentTime.toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  {[
                    { title: 'Active Employees', value: '168', change: '+12', changeType: 'positive', icon: 'Users', color: 'green' },
                    { title: 'Pending Applications', value: '24', change: '+8', changeType: 'positive', icon: 'UserPlus', color: 'blue' },
                    { title: 'Overdue Invoices', value: '8', change: '-3', changeType: 'positive', icon: 'AlertTriangle', color: 'amber' },
                    { title: 'Compliance Alerts', value: '3', change: '+1', changeType: 'negative', icon: 'Shield', color: 'red' }
                  ].map((kpi, index) => (
                    <KPICard
                      key={index}
                      title={kpi.title}
                      value={kpi.value}
                      change={kpi.change}
                      changeType={kpi.changeType}
                      icon={kpi.icon}
                      color={kpi.color}
                      previewMode={true}
                    />
                  ))}
                </div>

                {/* Charts Section - Responsive Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <ApplicantFunnelChart previewMode={true} />
                  <PayrollTrendChart previewMode={true} />
                </div>

                {/* Invoice Aging Chart */}
                <div className="mb-6 sm:mb-8">
                  <InvoiceAgingChart previewMode={true} />
                </div>

                {/* Activity and Actions Section - Responsive Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                  <RecentActivityFeed previewMode={true} />
                  <PendingActionItems previewMode={true} />
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Toast Notifications */}
        <NotificationToast
          notifications={toastNotifications}
          onDismiss={handleNotificationDismiss}
          position="top-right"
          autoHideDuration={5000}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <HeaderNavigation
        onSidebarToggle={handleSidebarToggle}
        user={user}
        userProfile={userProfile}
        notifications={mockNotifications}
      />

      {/* Main Sidebar */}
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div
        className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}
      >
        <div className="flex flex-col lg:flex-row min-h-[calc(100vh-4rem)]">
          {/* Dashboard Content */}
          <div
            className="flex-1 overflow-auto transition-all duration-300"
            style={{
              paddingRight: '0'
            }}
          >
            <div className="p-4 sm:p-6">
              {/* Breadcrumb Navigation */}
              <div className="mb-6">
                <BreadcrumbNavigation />
              </div>

              {/* Dashboard Header */}
              <div className="mb-6 sm:mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dashboard Overview</h1>
                    <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
                      Welcome back, {userProfile?.full_name || user?.email}. Here's what's happening with your staff placement operations.
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {currentTime.toLocaleDateString('en-IN', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <p className="text-base sm:text-lg font-semibold text-foreground">
                      {currentTime.toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="text-red-600">⚠️</div>
                    <div>
                      <p className="text-red-800 font-medium">Unable to load dashboard data</p>
                      <p className="text-red-600 text-sm">{error}</p>
                      <button
                        onClick={loadDashboardData}
                        className="text-red-700 underline text-sm mt-1"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-muted-foreground text-sm">Loading dashboard data...</p>
                  </div>
                </div>
              )}

              {/* KPI Cards */}
              {!loading && !error && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  {kpiData?.map((kpi, index) => (
                    <KPICard
                      key={index}
                      title={kpi.title}
                      value={kpi.value}
                      change={kpi.change}
                      changeType={kpi.changeType}
                      icon={kpi.icon}
                      color={kpi.color}
                    />
                  ))}
                </div>
              )}

              {/* Charts Section - Responsive Grid */}
              {!loading && !error && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <ApplicantFunnelChart data={funnelData} />
                    <PayrollTrendChart data={payrollTrendData} />
                  </div>

                  {/* Invoice and Performance */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <InvoiceAgingChart data={agingData} />
                    <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center">
                      <p className="text-muted-foreground italic">Additional Metrics and Reports coming soon...</p>
                    </div>
                  </div>

                  {/* Activity and Actions */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <RecentActivityFeed data={recentActivities} />
                    <PendingActionItems data={pendingActions} />
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Toast Notifications */}
      <NotificationToast
        notifications={toastNotifications}
        onDismiss={handleNotificationDismiss}
        position="top-right"
        autoHideDuration={5000}
      />
    </div>
  );
}